import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Zap } from 'lucide-react';
import { Box, Flex, Text, Container } from '@chakra-ui/react';
import DashboardPreview from '../ui/DashboardPreview';
import Button from '../ui/Button';

const Hero = () => {
  const navigate = useNavigate();

  const handleViewPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box
      as="section"
      position="relative"
      bg="landing.bg" borderBottomWidth="1px" borderColor="landing.border"
      pt={{ base: '100px', md: '140px' }}
      pb={{ base: 16, md: 24 }}
      overflow="hidden"
    >
      {/* Background gradients */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg="bg.hero"
        opacity={0.4}
        pointerEvents="none"
        zIndex={0}
      />
      
      {/* Background glow overlay */}
      <Box
        position="absolute"
        top="10%"
        left="50%"
        transform="translateX(-50%)"
        w={{ base: '300px', md: '800px' }}
        h={{ base: '300px', md: '800px' }}
        borderRadius="full"
        bg="bg.glow"
        opacity={0.5}
        pointerEvents="none"
        zIndex={0}
      />

      <Container maxW="1200px" position="relative" zIndex={1}>
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          align="center"
          gap={{ base: 12, lg: 8 }}
          justify="space-between"
        >
          {/* Content */}
          <Box flex={1} textAlign={{ base: 'center', lg: 'left' }} maxW={{ lg: '620px' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Badge */}
              <Flex
                display="inline-flex"
                align="center"
                gap={1.5}
                px={3} py={1.5}
                borderRadius="full"
                bg="brand.subtle"
                borderWidth="1px"
                borderColor="landing.border"
                mb={6}
              >
                <Box color="brand.primary" display="inline-flex"><Zap size={13} /></Box>
                <Text fontSize="xs" fontWeight="700" color="brand.primary" letterSpacing="0.05em">
                  Now live for Jira + ClickUp
                </Text>
              </Flex>

              {/* Title */}
              <Text
                as="h1"
                fontSize={{ base: '4xl', md: '5xl', lg: '6xl' }}
                fontWeight="800"
                color="text.main"
                letterSpacing="-0.03em"
                lineHeight="1.1"
                mb={5}
              >
                Keep Jira and ClickUp{' '}
                <Box
                  as="span"
                  display="inline-block"
                  position="relative"
                  color="brand.primary"
                  px={1}
                >
                  <Box
                    as="span"
                    position="absolute"
                    left="0"
                    right="0"
                    bottom="0.08em"
                    h="0.22em"
                    bg="brand.subtle"
                    borderRadius="sm"
                    zIndex={-1}
                  />
                  Perfectly in Sync.
                </Box>
              </Text>

              {/* Subtitle */}
              <Text fontSize="lg" color="text.secondary" mb={8} lineHeight="1.8" maxW="540px" mx={{ base: 'auto', lg: '0' }}>
                DatLynk keeps epics, tasks, subtasks, and mapped fields aligned across Jira and ClickUp, so teams can work in both tools without rebuilding structure.
              </Text>

              {/* Buttons & Trust Group */}
              <Box>
                <Flex gap={4} justify={{ base: 'center', lg: 'flex-start' }} align="center" wrap="wrap" mb={2}>
                  <Button
                    variant="cta"
                    size="large"
                    onClick={() => navigate('/signup')}
                    px={10}
                    py={7}
                    fontSize="lg"
                  >
                    <Flex align="center" gap={2}>
                      Get Started Free
                      <ArrowRight size={22} />
                    </Flex>
                  </Button>
                  <Button
                    variant="secondary"
                    size="large"
                    onClick={handleViewPricing}
                    bg="transparent"
                    borderWidth="1px"
                    borderColor="landing.border"
                    _hover={{ bg: 'landing.surface' }}
                    px={8}
                    py={6}
                  >
                    View Pricing
                  </Button>
                </Flex>
                
                {/* Fixed CTA Microcopy Row */}
                <Flex 
                  align="center" 
                  gap={2}
                  color="text.muted"
                  mt={2}
                  justify={{ base: 'center', lg: 'flex-start' }}
                >
                  <Text fontSize="xs" fontWeight="500" letterSpacing="0.02em">
                    Two-way sync • Hierarchy preserved • No manual cleanup
                  </Text>
                </Flex>
              </Box>

              {/* Integration Name Drop */}
              <Text fontSize="sm" color="text.muted" mt={5} fontWeight="500" textAlign={{ base: 'center', lg: 'left' }}>
                DatLynk is live today for <Box as="span" fontWeight="700" color="text.main">Jira and ClickUp</Box>, with more connectors planned.
              </Text>
            </motion.div>
          </Box>

          {/* Dashboard Preview */}
          <Box 
            flex={1.3} 
            w="full" 
            position="relative" 
            mt={{ base: 8, lg: 0 }} 
            mr={{ lg: '-4%' }}
          >
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <DashboardPreview />
            </motion.div>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default Hero;
